import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { SatelliteConfig, createOrbitPoints } from "./satellite-utils";

interface SatelliteProps {
  config: SatelliteConfig;
  speedFactor: number;
}

export interface SatelliteRef {
  getMesh: () => THREE.Mesh | null;
}

/**
 * Componente individual de satélite con trayectoria orbital y malla animada.
 * Usa useFrame para actualizar la posición por frame sin disparar re-renders de React.
 */
const Satellite = forwardRef<SatelliteRef, SatelliteProps>(
  ({ config, speedFactor }, ref) => {
    const meshRef = useRef<THREE.Mesh>(null);
    const angle = useRef(config.initialAngle);

    // Exponer malla via handle imperativo
    useImperativeHandle(ref, () => ({
      getMesh: () => meshRef.current,
    }));

    // Actualizar posición del satélite cada frame
    useFrame((state, delta) => {
      if (!meshRef.current) return;

      // Actualizar ángulo basado en velocidad orbital
      angle.current += delta * 1000 * config.speed * speedFactor;

      // Actualizar posición de la malla en el plano orbital
      meshRef.current.position.set(
        Math.cos(angle.current) * config.radius,
        0,
        Math.sin(angle.current) * config.radius
      );
    });

    // Generar puntos de la trayectoria orbital
    const orbitPoints = createOrbitPoints(config.radius);

    return (
      <group
        rotation-order="YXZ"
        rotation-x={config.inclination}
        rotation-y={config.heading}
      >
        {/* Trayectoria orbital */}
        <lineLoop>
          <bufferGeometry>
            {/* @ts-expect-error - espera `args` pero ya le estamos pasando todo lo necesario en las demás props */}
            <bufferAttribute
              attach="attributes-position"
              count={orbitPoints.length / 3}
              array={orbitPoints}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color={config.color} transparent opacity={0.4} />
        </lineLoop>

        {/* Malla del satélite */}
        <mesh ref={meshRef}>
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshBasicMaterial color={config.color} />
        </mesh>
      </group>
    );
  }
);

Satellite.displayName = "Satellite";

export default Satellite;
